/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import includes from 'lodash/includes';
import findIndex from 'lodash/findIndex';
import throttle from 'lodash/throttle';
import ScrollContainer from 'react-indiana-drag-scroll';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'lodash/fp';
import animateScrollTo from 'animated-scroll-to';
import { Container, Row, Column } from '../../../styles/Grid';
import { ModalsConsumer, modalIds } from '../../../contexts/ModalsContext';
import createButtonCss from '../../ui/Button/Button';
import OpenEnquiryFormButton from '../../ui/OpenEnquiryFormButton';
import getComponent from '../../../utils/getComponent';
import { getPrefilledSolution } from '../../../utils/getPrefilledSolutionValue';
import { NavConsumer, navbar_finite_state } from '../../../contexts/NavContext';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { COMPONENT_NAMES } from '../../../utils/constants';
import { SOLUTIONS_DICTIONARIES as dict } from '../../../utils/dictionarykeys';
import LinkWrapper from '../../ui/LinkWrapper';
import { StickyBarConsumer } from '../../../contexts/StickyBarContext';
import { withConsumer } from '../../../contexts';
import * as styles from './index.styles';

const stickyElementsSelector = '[data-sticky-id]';

const geosolutionHeaderId = COMPONENT_NAMES.geosolutionHeader;
const locationHeaderId = COMPONENT_NAMES.locationHeader;
const solutionHeroId = COMPONENT_NAMES.solutionHero;
const heroElementsIds = [geosolutionHeaderId, locationHeaderId, solutionHeroId];

const SCROLL_DIRECTIONS = {
  UP: 'UP',
  DOWN: 'DOWN',
};

// ID must match component name
const locationStickyComponentsLabels = {
  LocationHeader: 'StickyTabHero',
  Amenities: 'StickyTabAmenities',
  ContactAndMap: 'StickyTabContactAndMap',
  SolutionsCards: 'StickyTabSolutionCards',
};

const getStickyLabelFromComponent = component =>
  get(component, `fields.StickyLabel.value`, '');

export const componentHasStickyLabel = component =>
  getStickyLabelFromComponent(component) !== '';

export const getComponentsFromSitecore = sitecoreContext =>
  get(sitecoreContext, 'route.placeholders["jss-main"]', []);

const getLabelElements = sitecoreContext => {
  const components = getComponentsFromSitecore(sitecoreContext);
  const stickyLabels = components.reduce((acc, component) => {
    const label = getStickyLabelFromComponent(component);
    return label === '' ? acc : [...acc, { id: component.uid, label }];
  }, []);

  return stickyLabels;
};

const getLabelsForLocationPage = (sitecoreContext, t) => {
  const components = getComponentsFromSitecore(sitecoreContext);
  const stickyLabels = components.reduce((labelList, component) => {
    const label = locationStickyComponentsLabels[component.componentName];

    return !label
      ? labelList
      : [...labelList, { id: component.uid, label: t(label) }];
  }, []);

  return stickyLabels;
};

const getCurrentHeroInPage = sitecoreContext => {
  const components = getComponentsFromSitecore(sitecoreContext);
  const hero = find(components, ({ componentName }) =>
    includes(heroElementsIds, componentName),
  );

  return hero ? hero.componentName : null;
};

export class StickyBar extends SafePureComponent {
  // Needed to identify if it's positioned above the first sticky bar element to
  // disable active state.
  lastActiveElementIndex = -1;
  lastScrollPosition = 0;
  scrollDirection = SCROLL_DIRECTIONS.DOWN;
  fields = get(this.props, 'sitecoreContext.route.fields', {});
  state = {
    labels: [],
    selectedId: null,
    prefilledSolution: '',
    currentHeader: '',
  };

  barRef = React.createRef();
  scrollContainerRef = React.createRef();
  activeButtonRef = React.createRef();

  componentDidMount() {
    const { stickyBarContext, sitecoreContext } = this.props;

    this.heroComponent = getCurrentHeroInPage(sitecoreContext);
    this.stickyElements = Array.from(
      document.querySelectorAll(stickyElementsSelector),
    );

    this.initializePrefilledSolution();
    this.initializeLabels();
    stickyBarContext.init();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.selectedId !== this.state.selectedId) {
      this.scrollToSelectedButton();
    }
  }

  initializePrefilledSolution = () => {
    const { sitecoreContext, t } = this.props;
    const prefilledSolutionComponent = getPrefilledSolution(sitecoreContext);
    const isGeosolutionPage = this.heroComponent === geosolutionHeaderId;
    const prefilledSolution = get(
      prefilledSolutionComponent,
      'fields.Label.value',
      isGeosolutionPage ? t(dict.OFFICE_SPACE) : '',
    );

    this.setState({ prefilledSolution });
  };

  initializeLabels = () => {
    const { sitecoreContext, t } = this.props;
    const isLocationPage = this.heroComponent === locationHeaderId;
    const labels = isLocationPage
      ? getLabelsForLocationPage(sitecoreContext, t)
      : getLabelElements(sitecoreContext);

    if (labels.length >= 0) {
      this.setState({ labels });
      this.onScrollHandler();
      window.addEventListener('scroll', this.onScrollHandler);
    }
  };

  scrollToSelectedButton() {
    const container = get(this, 'scrollContainerRef.current.container.current');
    const activeButton = this.activeButtonRef.current;

    if (!activeButton) {
      return;
    }

    const containerOffsetLeft = container.getBoundingClientRect().left;
    const activeButtonPosition = activeButton.getBoundingClientRect().left;
    const newHorizontalScrollPosition =
      container.scrollLeft + activeButtonPosition - containerOffsetLeft;

    animateScrollTo([newHorizontalScrollPosition, 0], {
      elementToScroll: container,
      speed: 300,
    });
  }

  onScrollHandler = throttle(() => {
    this.spyForActiveElement();
    this.updateScrollDirection();
  }, 80);

  spyForActiveElement = () => {
    const { labels } = this.state;

    if (labels.length === 0) {
      return;
    }

    const windowCenter = window.innerHeight / 2;
    const elementInViewIndex = findIndex(this.stickyElements, element => {
      const rect = element.getBoundingClientRect();
      return rect.top < windowCenter && rect.bottom > windowCenter;
    });
    const isPositionedBeforeFirstElement =
      elementInViewIndex === -1 &&
      this.lastActiveElementIndex === 0 &&
      this.scrollDirection === SCROLL_DIRECTIONS.UP;

    if (isPositionedBeforeFirstElement) {
      this.setState({ selectedId: -1 });
      return;
    }

    if (elementInViewIndex >= 0) {
      const element = labels[elementInViewIndex];
      if (element) {
        this.setState({ selectedId: element.id });
        this.lastActiveElementIndex = elementInViewIndex;
      }
    }
  };

  updateScrollDirection = () => {
    this.scrollDirection =
      this.lastScrollPosition < window.scrollY
        ? SCROLL_DIRECTIONS.DOWN
        : SCROLL_DIRECTIONS.UP;
    this.lastScrollPosition = window.scrollY;
  };

  renderGetInTouchCTA(linkObject, prefilledValues) {
    const openEnquiryFormButtonProps = {
      linkObject,
      prefilledValues: prefilledValues
        ? prefilledValues
        : this.getEnquiryDefaultValues(
            this.fields,
            this.state.prefilledSolution,
          ),
      customCss: styles.getInTouchCss,
    };

    return <OpenEnquiryFormButton {...openEnquiryFormButtonProps} />;
  }

  getLocationValue(hero, fields) {
    switch (hero) {
      case geosolutionHeaderId:
        return get(fields, 'GeoName.value', '');
      case locationHeaderId:
        return get(fields, 'ProtonName.value', '');
      case solutionHeroId:
        return '';
      default:
        return null;
    }
  }

  getEnquiryDefaultValues = (fields, prefilledSolution) => {
    return {
      Location: this.getLocationValue(this.heroComponent, fields),
      HowManyPeople: get(
        this.howManyPeopleSelect,
        'current.state.selectedLabel',
        '',
      ),
      MoveInDate: get(this.moveInDateSelect, 'current.state.selectedLabel', ''),
      Solution: prefilledSolution,
    };
  };

  scrollToComponent(id) {
    const component = document.querySelector(`[data-sticky-id="${id}"]`);

    if (component) {
      const offset = this.barRef.current.clientHeight;
      const componentScrollPosition = component.getBoundingClientRect().top;
      const newScrollPosition =
        componentScrollPosition + window.pageYOffset - offset;

      window.removeEventListener('scroll', this.onScrollHandler);

      animateScrollTo(newScrollPosition).then(hasScrolledToPosition => {
        window.addEventListener('scroll', this.onScrollHandler);

        if (hasScrolledToPosition) {
          this.setState({ selectedId: id });
        }
      });
    }
  }

  renderLabels = () => {
    const { selectedId, labels } = this.state;
    const { t } = this.props;

    return labels.map(({ id, label }) => (
      <button
        css={[styles.buttonCss, id === selectedId && styles.selectedButtonCss]}
        key={id}
        onClick={() => this.scrollToComponent(id)}
        ref={id === selectedId ? this.activeButtonRef : null}
      >
        {t(label)}
      </button>
    ));
  };

  renderButtons = () => {
    switch (this.heroComponent) {
      case geosolutionHeaderId:
        return this.renderButtonsForGeosolutionHeader();
      case locationHeaderId:
        return this.renderButtonsForLocationHeader();
      case solutionHeroId:
        return this.renderButtonsForSolutionHero();
      default:
        return null;
    }
  };

  renderButtonsForGeosolutionHeader = () => {
    const { t, sitecoreContext, stickyBarContext } = this.props;
    const { isOneButtonLayout } = stickyBarContext;

    const geoSolutionComponent = getComponent(
      sitecoreContext,
      geosolutionHeaderId,
    );

    const SeeLocationsCTA = get(
      geoSolutionComponent,
      'fields.SeeLocationsCTA',
      { value: '' },
    );

    const GetInTouchCTA = get(geoSolutionComponent, 'fields.GetInTouchCTA', {
      value: { href: '' },
    });

    const moreInCta = {
      value: {
        text: `${t('SeeLocations')}`,
        linktype: 'internal',
        url: SeeLocationsCTA.value,
        href: SeeLocationsCTA.value,
      },
    };

    return (
      <div
        css={[
          styles.heroButtonsContentCss,
          isOneButtonLayout() && styles.oneButtonContentCss,
        ]}
      >
        {this.renderGetInTouchCTA(GetInTouchCTA)}
        {!isOneButtonLayout() && (
          <LinkWrapper type="secondary" field={moreInCta} />
        )}
      </div>
    );
  };

  renderButtonsForLocationHeader = () => {
    const { t, sitecoreContext } = this.props;

    const locationHeaderComponent = getComponent(
      sitecoreContext,
      locationHeaderId,
    );

    const GetInTouchCTA = get(
      locationHeaderComponent,
      'fields.ExternalCTA',
      {},
    );

    const prefilledValues = this.getEnquiryDefaultValues(
      this.fields,
      this.state.prefilledSolution,
    );

    {
      /* TODO: have a component to manage Book a tour buttons same as enquiry forms */
    }
    const renderBookNowButton = () => (
      <ModalsConsumer>
        {({ openModal }) => (
          <button
            css={createButtonCss('secondary')}
            onClick={() => openModal(modalIds.bookTour)}
          >
            {t('BookAVisitCTA')}
          </button>
        )}
      </ModalsConsumer>
    );

    return (
      <div css={styles.heroButtonsContentCss}>
        {this.renderGetInTouchCTA(GetInTouchCTA, prefilledValues)}
        {renderBookNowButton()}
      </div>
    );
  };

  renderSecondaryCTAForSolutionHero = solutionHero => {
    const { SecondaryCTA, HideSecondaryCTA } = get(solutionHero, 'fields', {});
    const { t } = this.props;
    const secondaryCTALink = get(SecondaryCTA, 'value.href');
    const hasSecondaryCTALink =
      !!secondaryCTALink && secondaryCTALink !== 'http://';
    const secondaryCTALabel = get(SecondaryCTA, 'value.text', t('FindCTA'));
    const shouldHideSecondaryCTA = get(HideSecondaryCTA, 'value', false);

    if (shouldHideSecondaryCTA) {
      return null;
    }

    if (hasSecondaryCTALink) {
      return <LinkWrapper field={SecondaryCTA} type="secondary" />;
    }

    return (
      <NavConsumer>
        {({ openModal }) => (
          <button
            css={createButtonCss('secondary')}
            data-id={navbar_finite_state.SEARCH}
            onClick={openModal}
          >
            {secondaryCTALabel}
          </button>
        )}
      </NavConsumer>
    );
  };

  renderButtonsForSolutionHero = () => {
    const { sitecoreContext, stickyBarContext } = this.props;
    const { isOneButtonLayout } = stickyBarContext;
    const solutionHero = getComponent(sitecoreContext, 'SolutionHero');
    const GetInTouchCTA = get(solutionHero, 'fields.GetInTouchCTA', {
      value: { href: '' },
    });

    return (
      <div
        css={[
          styles.heroButtonsContentCss,
          isOneButtonLayout() && styles.oneButtonContentCss,
        ]}
      >
        {this.renderGetInTouchCTA(GetInTouchCTA)}
        {this.renderSecondaryCTAForSolutionHero(solutionHero)}
      </div>
    );
  };

  safeRender() {
    const { labels } = this.state;
    const { stickyBarContext } = this.props;

    if (labels.length === 0) return null;

    return (
      <div
        css={[
          styles.containerCss,
          stickyBarContext.isActive && styles.stickyShownDesktopCss,
        ]}
        data-testid="sticky-bar"
        aria-expanded={stickyBarContext.isActive}
      >
        <Container className={styles.containerExtraCss}>
          <Row>
            <Column cols={{ sm: 6, md: 8 }}>
              <div css={styles.contentCss}>
                <div css={styles.buttonsContainerCss} ref={this.barRef}>
                  <ScrollContainer
                    css={styles.scrollContainerCss}
                    ref={this.scrollContainerRef}
                  >
                    {this.renderLabels()}
                  </ScrollContainer>
                </div>
                <div
                  data-sticky-bottom-bar
                  css={[
                    styles.heroButtonsContainerCss,
                    stickyBarContext.isActive && styles.showHeroButtonsCss,
                    stickyBarContext.isOneButtonLayout() &&
                      styles.oneButtonContainerCss,
                  ]}
                  data-testid="sticky-buttons-container"
                >
                  {this.renderButtons()}
                </div>
              </div>
            </Column>
          </Row>
        </Container>
      </div>
    );
  }
}

StickyBar.displayName = 'StickyBar';

StickyBar.defaultProps = { fields: {}, t: key => key };

export default compose(
  withNamespaces(),
  withSitecoreContext(),
  withConsumer(StickyBarConsumer, 'stickyBarContext'),
)(StickyBar);
